import { useState } from "react";
import clsx from "clsx";
import { HistoryDomainNav } from "../../components/HistoryDomainNav.tsx";
import { EmptyState } from "../../components/EmptyState.tsx";
import { RetiredPlayers } from "../../components/RetiredPlayers.tsx";
import useTitleBar from "../../hooks/useTitleBar.tsx";
import {
  MobileContentHero,
  MobileExpertDisclosure,
  MobileStatRow,
  MobileTimeline,
} from "../../mobile/mobilePrimitives.tsx";
import { MobileP3ShareCard } from "../../mobile/MobileP3ShareExportActions.tsx";
import AwardsAndChamp from "./AwardsAndChamp.tsx";
import Team from "./Team.tsx";
import type { View } from "../../../../../common/types.ts";
import { helpers } from "../../util/helpers.ts";
import { formatSeasonRecord } from "../../util/seasonRecord.ts";
import i18n from "../../util/i18n.ts";
import useIsMobileViewport from "../../hooks/useIsMobileViewport.ts";
import { AWARD_NAMES } from "../../util/awardNames.ts";
import { formatPlayerNameFromLegacyName } from "../../util/playerNames.ts";
import { createMobileReturnUrl } from "../../util/mobilePageContext.ts";
import { AchievementsSheet } from "../../components/AchievementsSheet.tsx";
import { translateTeamName, translateTeamRegion } from "../../util/teamNames.ts";
import {
  getHistoryBestRecordTeams,
  getHistoryMajorAwards,
  getHistorySeasonArchiveCounts,
  hasHistoryChampionStory,
} from "./seasonStory.ts";
export type ActualProps = Exclude<View<"history">, { invalidSeason: true; season: number }>;

type MobileHistorySummaryTab = "awards" | "teams" | "retired";

const getMobileScrollY = () => (typeof window !== "undefined" ? window.scrollY : undefined);

const createHistoryPlayerHref = (pid: number, season: number) =>
  createMobileReturnUrl(helpers.leagueUrl(["player", pid]), {
    activeTab: String(season),
    filterKey: "historySeason",
    filterValue: season,
    pageID: "history",
    scrollY: getMobileScrollY(),
  });

const History = (props: View<"history">) => {
  const { invalidSeason, season } = props;
  const [activeMobileHistorySummaryTab, setActiveMobileHistorySummaryTab] =
    useState<MobileHistorySummaryTab>("awards");
  // M3-3: the achievements trophy case is no longer a standalone page; it opens
  // in place as a MobileSheet from the history hub (component state, no
  // navigation and no back-stack entry — see MOBILE_SHEET_CONTRACT_V1).
  const [achievementsSheetOpen, setAchievementsSheetOpen] = useState(false);
  const isMobileViewport = useIsMobileViewport();

  useTitleBar({
    title: invalidSeason
      ? i18n.t("history.unavailableTitle", {
          ns: "ui",
          defaultValue: "Season summary unavailable",
        })
      : i18n.t("history.title", {
          ns: "ui",
          defaultValue: "Season Summary",
        }),
    jumpTo: !window.mobile,
    jumpToSeason: season,
    dropdownView: "history",
    dropdownFields: {
      seasonsHistory: season,
    },
  });
  // R4b: the in-page prev/next/recent-complete season cursor (MobileArchiveSeasonNav)
  // is retired here — season switching is the single title-bar season chip
  // (useTitleBar dropdownFields.seasonsHistory above), mirroring the R3-1 removal of
  // the DailySchedule "choose date" panel. The invalid-season recovery link below and
  // the other SeasonNav hosts (draft history / lottery / scouting) are unaffected.
  const recentCompleteHref = helpers.leagueUrl(["history"]);

  if (invalidSeason) {
    return (
      <div data-history-invalid-season={season}>
        <EmptyState
          action={
            <a
              className="btn btn-primary"
              data-history-invalid-season-recovery=""
              href={recentCompleteHref}
            >
              {i18n.t("history.mobileRecentCompleteAction", {
                ns: "ui",
                defaultValue: "Open recent complete season",
              })}
            </a>
          }
          reason="not-started"
          title={i18n.t("history.unavailableTitle", {
            ns: "ui",
            defaultValue: "Season summary unavailable",
          })}
        >
          {i18n.t("history.unavailableBody", {
            ns: "ui",
            defaultValue:
              "This season summary has not been generated yet. It appears after the season is complete.",
          })}
        </EmptyState>
      </div>
    );
  }

  const { awards, champ, confs, retiredPlayers, userTid } = props as ActualProps;
  const archiveCounts = getHistorySeasonArchiveCounts({
    awards,
    retiredPlayers,
  });
  const bestRecordTeams = getHistoryBestRecordTeams(awards);
  const championName = champ
    ? `${translateTeamRegion(champ.seasonAttrs.region)} ${translateTeamName(champ.seasonAttrs.name)}`
    : undefined;
  const finalsMvp = awards.finalsMvp;
  const hasCompleteChampionStory = hasHistoryChampionStory({ awards, champ });
  const majorAwardCount = getHistoryMajorAwards(awards).filter(({ award }) => award).length;
  const userTeamWonTitle = champ?.tid === userTid;
  const userTeamHadBestRecord = bestRecordTeams.some((team) => team.tid === userTid);
  const userTeamPosition = userTeamWonTitle
    ? i18n.t("history.mobileUserTeamChampion", {
        ns: "ui",
        defaultValue: "Champion",
      })
    : userTeamHadBestRecord
      ? i18n.t("history.mobileUserTeamBestRecord", {
          ns: "ui",
          defaultValue: "Best record",
        })
      : i18n.t("history.mobileUserTeamArchived", {
          ns: "ui",
          defaultValue: "Archived season",
        });
  const renderAwardPlayer = (award: ActualProps["awards"][number]) =>
    award ? (
      <a href={createHistoryPlayerHref(award.pid, season)}>
        {formatPlayerNameFromLegacyName(award.name)}
      </a>
    ) : (
      <span className="text-body-secondary">
        {i18n.t("history.mobileAwardMissing", {
          ns: "ui",
          defaultValue: "Not awarded",
        })}
      </span>
    );
  const renderAwardDetail = (award: ActualProps["awards"][number]) =>
    award
      ? i18n.t("history.mobileAwardStats", {
          ns: "ui",
          defaultValue: "{{pts}} pts / {{trb}} reb / {{ast}} ast",
          // G2b-2 carried fix: coerce missing stats to 0 so a partial award
          // record never renders empty segments ("pts / 2.8 reb / ast").
          ast: helpers.roundStat(award.ast ?? 0, "ast"),
          pts: helpers.roundStat(award.pts ?? 0, "pts"),
          trb: helpers.roundStat(award.trb ?? 0, "trb"),
        })
      : i18n.t("history.mobileAwardMissing", {
          ns: "ui",
          defaultValue: "Not awarded",
        });
  const renderBestRecordTeams = () =>
    bestRecordTeams.length > 0 ? (
      <>
        {bestRecordTeams.map((team, index) => (
          <span key={team.tid}>
            {index > 0 ? " / " : null}
            <a href={helpers.leagueUrl(["roster", `${team.abbrev}_${team.tid}`, season])}>
              {translateTeamRegion(team.region)} {translateTeamName(team.name)}
            </a>{" "}
            ({formatSeasonRecord(team)})
          </span>
        ))}
      </>
    ) : (
      i18n.t("history.mobileAwardMissing", {
        ns: "ui",
        defaultValue: "Not awarded",
      })
    );

  const allLeagueTeamsLabel = i18n.t("history.allLeagueTeams", {
    ns: "ui",
    defaultValue: "All-League Teams",
  });
  const allDefensiveTeamsLabel = i18n.t("history.allDefensiveTeams", {
    ns: "ui",
    defaultValue: "All-Defensive Teams",
  });
  const allRookieTeamLabel = i18n.t("history.allRookieTeam", {
    ns: "ui",
    defaultValue: "All-Rookie Team",
  });
  const seasonStoryTimelineItems = [
    champ
      ? {
          detail: championName,
          key: "champion",
          label: season,
          meta: i18n.t("history.mobileChampionTimelineMeta", {
            ns: "ui",
            defaultValue: "League champion",
          }),
          title: i18n.t("history.mobileChampionTimelineTitle", {
            ns: "ui",
            defaultValue: "Champion crowned",
          }),
        }
      : undefined,
    finalsMvp
      ? {
          detail: renderAwardPlayer(finalsMvp),
          key: "finalsMvp",
          label: i18n.t("history.mobileFinalsMvpShort", {
            ns: "ui",
            defaultValue: "FMVP",
          }),
          meta: i18n.t("history.mobileFinalsMvpTimelineMeta", {
            ns: "ui",
            defaultValue: "Finals award",
          }),
          title: i18n.t("history.mobileFinalsMvpTimelineTitle", {
            ns: "ui",
            defaultValue: "Finals MVP named",
          }),
        }
      : undefined,
    bestRecordTeams.length > 0
      ? {
          detail: <span>{renderBestRecordTeams()}</span>,
          key: "bestRecord",
          label: i18n.t("history.mobileBestRecordShort", {
            ns: "ui",
            defaultValue: "Record",
          }),
          meta: i18n.t("history.mobileBestRecordTimelineMeta", {
            ns: "ui",
            defaultValue: "Regular season",
          }),
          title: i18n.t("history.mobileBestRecordTimelineTitle", {
            ns: "ui",
            defaultValue: "Best record set",
          }),
        }
      : undefined,
    majorAwardCount > 0
      ? {
          detail: i18n.t("history.mobileMajorAwardsTimelineDetail", {
            ns: "ui",
            defaultValue: "{{count}} major awards have winners.",
            count: majorAwardCount,
          }),
          key: "majorAwards",
          label: i18n.t("history.mobileAwardsShort", {
            ns: "ui",
            defaultValue: "Awards",
          }),
          meta: i18n.t("history.mobileMajorAwardsTimelineMeta", {
            ns: "ui",
            defaultValue: "Season honors",
          }),
          title: i18n.t("history.mobileMajorAwardsTimelineTitle", {
            ns: "ui",
            defaultValue: "Major awards summarized",
          }),
        }
      : undefined,
  ].filter((item) => item !== undefined);

  const awardsAndChamp = (
    <AwardsAndChamp awards={awards} champ={champ} confs={confs} season={season} userTid={userTid} />
  );
  const awardTeams = (
    <div data-all-league-layout="">
      <Team
        className="mb-3"
        name={allLeagueTeamsLabel}
        nested
        season={season}
        team={awards.allLeague}
        userTid={userTid}
      />
      <Team
        className="mb-3"
        name={allDefensiveTeamsLabel}
        nested
        season={season}
        team={awards.allDefensive}
        userTid={userTid}
      />
      <Team
        className="mb-3"
        name={allRookieTeamLabel}
        season={season}
        team={awards.allRookie}
        userTid={userTid}
      />
    </div>
  );
  const retiredPlayersSection = (
    <RetiredPlayers retiredPlayers={retiredPlayers} season={season} userTid={userTid} />
  );
  const mobileHistorySummaryTabs = [
    {
      id: "awards",
      label: i18n.t("history.awardsTab", {
        ns: "ui",
        defaultValue: "Awards",
      }),
      content: awardsAndChamp,
    },
    {
      id: "teams",
      label: i18n.t("history.teamsTab", {
        ns: "ui",
        defaultValue: "Teams",
      }),
      content: awardTeams,
    },
    {
      id: "retired",
      label: i18n.t("history.retiredTab", {
        ns: "ui",
        defaultValue: "Retired",
      }),
      content: retiredPlayersSection,
    },
  ] as const;
  const activeMobileHistorySummaryTabConfig = mobileHistorySummaryTabs.find(
    (tab) => tab.id === activeMobileHistorySummaryTab,
  )!;
  const finalsMvpName = finalsMvp ? formatPlayerNameFromLegacyName(finalsMvp.name) : undefined;
  const seasonReviewTitle = i18n.t("history.p2s05SeasonReviewTitle", {
    ns: "ui",
    season,
    defaultValue: "{{season}} season review",
  });
  const seasonShareTitle =
    i18n.t("history.p3ShareTitle", {
      ns: "ui",
      defaultValue: "{{season}} Season Summary",
      season,
    }) || `${season} Season Summary`;
  const seasonShareText =
    i18n.t("history.p3ShareText", {
      ns: "ui",
      champion:
        championName ??
        i18n.t("history.mobileAwardMissing", {
          ns: "ui",
          defaultValue: "Not awarded",
        }),
      defaultValue:
        "Champion: {{champion}}. Finals MVP: {{finalsMvp}}. Major awards summarized: {{majorAwardCount}}.",
      finalsMvp:
        finalsMvpName ??
        i18n.t("history.mobileAwardMissing", {
          ns: "ui",
          defaultValue: "Not awarded",
        }),
      majorAwardCount,
    }) ||
    `Champion: ${championName ?? "Not awarded"}. Finals MVP: ${
      finalsMvpName ?? "Not awarded"
    }. Major awards summarized: ${majorAwardCount}.`;

  return (
    <div data-history-season={season}>
      <section className="mb-3">
        <div>
          <div className="small text-body-secondary">
            <span>
              {i18n.t("history.p2s03RecentCompletedReview", {
                ns: "ui",
                defaultValue: "Recent completed season review",
              })}
            </span>
            <span className="visually-hidden">
              {i18n.t("history.p2s05RecentSeason", {
                ns: "ui",
                season,
                defaultValue: "Recent season: {{season}}",
              })}
            </span>
          </div>
          <h1 className="h4 mb-0">
            <span>{seasonReviewTitle}</span>
          </h1>
        </div>
        {/* G2b-2 carried fix: these badges repeated the h1 season and the champion
            hero on the first screen (法则二). Kept as SR-only bookkeeping anchors
            (cross-page markers/keys preserved) rather than visible duplicates.
            R4b: the "browse history" jump-link was retired with the in-page season
            cursor — season switching is the title-bar chip now. */}
        <div className="visually-hidden">
          <span className="badge text-bg-light border">
            {i18n.t("history.p2s08CurrentSeasonStory", {
              ns: "ui",
              season,
              defaultValue: "{{season}} season story",
            })}
          </span>
          <span className="badge text-bg-light border">
            {i18n.t("history.p2s08RecentChampion", {
              ns: "ui",
              defaultValue: "Recent champion: {{champion}}",
              champion:
                championName ??
                i18n.t("history.none", {
                  ns: "ui",
                  defaultValue: "None",
                }),
            })}
          </span>
        </div>
        <div className="small text-body-secondary mt-1">
          {i18n.t("history.p2s05CurrentStageHidden", {
            ns: "ui",
            defaultValue: "You're viewing a past season.",
          })}
        </div>
      </section>

      <MobileContentHero
        className="mb-3"
        data-champion-hero=""
        data-season-story={hasCompleteChampionStory ? "complete" : "partial"}
        title={
          championName
            ? i18n.t("history.mobileChampionTitle", {
                ns: "ui",
                defaultValue: "{{team}} won the {{season}} title",
                season,
                team: championName,
              })
            : i18n.t("history.mobileChampionTitleMissing", {
                ns: "ui",
                defaultValue: "{{season}} champion unavailable",
                season,
              })
        }
      >
        <div data-major-awards-hierarchy="">
          <div className="mb-2 fw-semibold" data-finals-mvp="">
            {i18n.t("history.mobileFinalsMvp", {
              ns: "ui",
              defaultValue: "Finals MVP",
            })}
            : {renderAwardPlayer(finalsMvp)}
          </div>
          <MobileStatRow
            detail={
              userTeamWonTitle
                ? championName
                : userTeamHadBestRecord
                  ? renderBestRecordTeams()
                  : // G2b-2 carried fix: dropped the self-narration line
                    // ("Use the archive below…", audit word "背景"); the row
                    // value already states the archived position.
                    undefined
            }
            label={i18n.t("history.mobileUserTeamPosition", {
              ns: "ui",
              defaultValue: "Your team",
            })}
            tone={userTeamWonTitle || userTeamHadBestRecord ? "positive" : "neutral"}
            value={<span>{userTeamPosition}</span>}
          />
          {getHistoryMajorAwards(awards).map(({ award, key }) => (
            <MobileStatRow
              detail={renderAwardDetail(award)}
              key={key}
              label={AWARD_NAMES[key]}
              tone={award?.tid === userTid ? "positive" : "neutral"}
              value={renderAwardPlayer(award)}
            />
          ))}
        </div>
      </MobileContentHero>

      <MobileTimeline
        className="mb-3"
        data-season-story-timeline=""
        emptyLabel={i18n.t("history.mobileSeasonTimelineEmpty", {
          ns: "ui",
          defaultValue: "No season story entries are available yet.",
        })}
        items={seasonStoryTimelineItems}
        title={i18n.t("history.mobileSeasonTimelineTitle", {
          ns: "ui",
          defaultValue: "Season story timeline",
        })}
      />

      {/* U6-4: season title, champion conclusion, and story timeline lead the
          archive. Achievements remain an in-place sheet, but its entry is
          secondary to that core season story and creates no URL/back-stack
          entry (MOBILE_SHEET_CONTRACT_V1 in MobileSheet.tsx). */}
      <button
        className="btn btn-outline-secondary w-100 d-flex flex-column align-items-start mb-3"
        data-history-achievements-entry=""
        onClick={() => setAchievementsSheetOpen(true)}
        type="button"
      >
        <span className="fw-semibold">
          {i18n.t("achievements.entryLink", {
            ns: "ui",
            defaultValue: "View your achievements",
          })}
        </span>
        <span className="small text-body-secondary">
          {i18n.t("achievements.entrySubtitle", {
            ns: "ui",
            defaultValue: "Your GM trophy case",
          })}
        </span>
      </button>
      <AchievementsSheet
        onClose={() => setAchievementsSheetOpen(false)}
        open={achievementsSheetOpen}
      />

      {/* U6-4: Market-domain draft archives are explicit, folded cross-domain
          exits after the League season conclusion. Each link preserves this
          exact history root/current/past URL for the global return control. */}
      <HistoryDomainNav />

      <MobileExpertDisclosure
        className="mt-3"
        expertHostId="history-complete-archive"
        count={i18n.t("history.mobileCompleteArchiveCount", {
          ns: "ui",
          defaultValue:
            "{{allLeague}} All-League / {{allDefensive}} All-Defensive / {{allRookie}} rookies / {{retired}} retired",
          allDefensive: archiveCounts.allDefensive,
          allLeague: archiveCounts.allLeague,
          allRookie: archiveCounts.allRookie,
          retired: archiveCounts.retired,
        })}
        data-complete-history-archive=""
        open={!isMobileViewport}
        summary={i18n.t("history.mobileCompleteArchive", {
          ns: "ui",
          defaultValue: "Complete season archive",
        })}
      >
        <details className="mb-3" data-native-details-host="history-share">
          <summary className="btn btn-outline-secondary btn-sm">
            {i18n.t("history.p3ShareSummary", {
              ns: "ui",
              defaultValue: "Share season summary",
            })}
          </summary>
          <MobileP3ShareCard
            className="mt-2"
            kind="seasonSummary"
            payload={{
              text: seasonShareText,
              title: seasonShareTitle,
              url: helpers.leagueUrl(["history", season]),
            }}
          />
        </details>
        {isMobileViewport ? (
          <section
            aria-label={i18n.t("history.mobileSummaryTabs", {
              ns: "ui",
              defaultValue: "Season summary sections",
            })}
            className="mobile-table-tabs"
            data-mobile-table-tabs={mobileHistorySummaryTabs.length}
          >
            <div className="mobile-table-tablist" role="tablist">
              {mobileHistorySummaryTabs.map((tab) => (
                <button
                  aria-controls={`history-mobile-summary-panel-${tab.id}`}
                  aria-selected={activeMobileHistorySummaryTab === tab.id}
                  className={clsx("mobile-table-tab", {
                    "mobile-table-tab-active": activeMobileHistorySummaryTab === tab.id,
                  })}
                  id={`history-mobile-summary-tab-${tab.id}`}
                  key={tab.id}
                  onClick={() => {
                    setActiveMobileHistorySummaryTab(tab.id);
                  }}
                  role="tab"
                  type="button"
                >
                  <span className="mobile-table-tab-label">{tab.label}</span>
                </button>
              ))}
            </div>
            <div
              aria-labelledby={`history-mobile-summary-tab-${activeMobileHistorySummaryTab}`}
              className="mobile-table-panel"
              id={`history-mobile-summary-panel-${activeMobileHistorySummaryTab}`}
              role="tabpanel"
            >
              {activeMobileHistorySummaryTabConfig.content}
            </div>
          </section>
        ) : (
          <div className="row" data-all-league-layout="">
            <div className="col-md-3 col-sm-4 col-12">{awardsAndChamp}</div>
            <div className="col-md-3 col-sm-4 col-12">
              <Team
                name={allLeagueTeamsLabel}
                nested
                season={season}
                team={awards.allLeague}
                userTid={userTid}
              />
            </div>
            <div className="col-md-3 col-sm-4 col-12">
              <Team
                className="mb-3"
                name={allDefensiveTeamsLabel}
                nested
                season={season}
                team={awards.allDefensive}
                userTid={userTid}
              />
              <Team
                className="mb-3"
                name={allRookieTeamLabel}
                season={season}
                team={awards.allRookie}
                userTid={userTid}
              />
            </div>
            <div className="col-md-3 col-sm-12">{retiredPlayersSection}</div>
          </div>
        )}
      </MobileExpertDisclosure>
    </div>
  );
};

export default History;
